<template>
    <template v-if="isButtonVariant">
        <BaseButton
            theme="neutral"
            @click="handleClick()"
            @mouseenter="emit('insight-hover', useCase)"
            @mouseleave="emit('insight-hover-end')"
            @focus="emit('insight-hover', useCase)"
            @blur="emit('insight-hover-end')">
            {{ useCase.label }}
        </BaseButton>
    </template>
    <template v-else>
        <BaseCard
            as="button"
            padding="sm"
            radius="lgr"
            type="button"
            :aria-label="`Start conversation: ${useCase.label}`"
            class="flex w-full flex-col gap-1 text-left"
            @mouseenter="emit('insight-hover', useCase)"
            @focus="emit('insight-hover', useCase)"
            @mouseleave="emit('insight-hover-end')"
            @blur="emit('insight-hover-end')"
            @click="handleClick()">
            <div class="flex items-center gap-2">
                <img v-if="useCase.icon_url" :src="useCase.icon_url" alt="" aria-hidden="true" class="size-5 shrink-0 object-contain" />
                <component :is="icon" v-else-if="icon" class="h-3.5 w-6 shrink-0" />
                <h3 class="body-bold text-ink">{{ useCase.label }}</h3>
            </div>
            <p class="body-small whitespace-pre-wrap text-ink-muted">{{ useCase.description }}</p>
        </BaseCard>
    </template>
</template>

<script setup lang="ts">
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import type { ChatEntranceUseCaseData } from "~vue/types/chat";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { type Component, computed } from "vue";

import { ChatUseCaseDisplayVariant, ChatUseCaseLocation } from "./utils";

interface Props {
    useCase: ChatEntranceUseCaseData;
    icon?: Component;
    variant: ChatUseCaseDisplayVariant;
    location: ChatUseCaseLocation;
}

const props = defineProps<Props>();

const isButtonVariant = computed(() => props.variant === ChatUseCaseDisplayVariant.BUTTON);

const emit = defineEmits<{
    (e: "insight-hover", useCase: ChatEntranceUseCaseData): void;
    (e: "insight-hover-end"): void;
    (e: "insight-clicked", useCase: ChatEntranceUseCaseData): void;
}>();

const extraLogData = computed(() => ({
    label: props.useCase.label,
    call_code: props.useCase.call_code,
    chat_source: props.useCase.chat_source,
    description: props.useCase.description,
    icon_url: props.useCase.icon_url,
    variant: props.variant,
    location: props.location,
}));

function handleClick() {
    logUserInteraction(AmplitudeEventNames.CHAT_USE_CASE_ENTRANCE_CLICKED, extraLogData.value);
    emit("insight-clicked", props.useCase);
}
</script>
